// src/pages/Contact.tsx
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Clock, Send, MessageSquare } from 'lucide-react';
import { staggerContainer, fadeInUp } from '../utils/animations';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const Contact: React.FC = () => {
// In Contact.tsx
  const { ref, controls } = useScrollAnimation<HTMLDivElement>();  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success'>('idle');
  const [selectedTopic, setSelectedTopic] = useState('general');

  const contactInfo = [
    {
      icon: <Phone size={24} />,
      title: 'Phone & WhatsApp',
      details: [
        'Main Line: 059 419 7765',
        'WhatsApp: 027 704 1765',
        'Momo: 053 885 4093'
      ],
      color: 'from-blue-400 to-blue-600'
    },
    {
      icon: <Mail size={24} />,
      title: 'Email',
      details: [
        'Bookings: getnailedbyaj@gmail.com',
        'General: getnailedbyaj@gmail.com',
        'Corporate: getnailedbyaj@gmail.com'
      ],
      color: 'from-purple-400 to-purple-600'
    },
    {
      icon: <MapPin size={24} />,
      title: 'Location',
      details: [
        'Westlands Blvd',
        'Hatso, Accra',
        'Ghana'
      ],
      color: 'from-green-400 to-green-600'
    },
    {
      icon: <Clock size={24} />,
      title: 'Hours',
      details: [
        'Mon-Fri: 9:00 AM - 7:00 PM',
        'Saturday: 9:00 AM - 6:00 PM',
        'Sunday: 11:00 AM - 5:00 PM'
      ],
      color: 'from-orange-400 to-orange-600'
    }
  ];

  const topics = [
    { id: 'general', label: 'General Inquiry' },
    { id: 'booking', label: 'Booking Question' },
    { id: 'corporate', label: 'Corporate Event' },
    { id: 'custom', label: 'Custom Design' },
    { id: 'complaint', label: 'Complaint' },
    { id: 'partnership', label: 'Partnership' }
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormStatus('submitting');
    
    // Simulate form submission
    setTimeout(() => {
      setFormStatus('success');
      setTimeout(() => setFormStatus('idle'), 3000);
    }, 1500);
  };

  return (
    <motion.main
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen"
    >
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-mauve-50 to-white pt-24 pb-16">
        <div className="container-custom text-center">
          <h1 className="text-5xl font-bold text-mauve-900 mb-6">
            Contact Us
          </h1>
          <p className="text-xl text-mauve-700 max-w-3xl mx-auto">
            Get in touch with our team in Accra. We're here to answer your questions 
            and help you book your perfect nail experience.
          </p>
        </div>
      </section>

      {/* Contact Information */}
      <section ref={ref} className="section-padding bg-white">
        <motion.div
          initial="initial"
          animate={controls}
          variants={staggerContainer}
          className="container-custom"
        >
          <motion.div variants={fadeInUp} className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {contactInfo.map((item, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                custom={index}
                className="bg-white rounded-2xl shadow-lg border border-mauve-100 overflow-hidden hover:shadow-xl transition-shadow"
              >
                <div className={`bg-gradient-to-r ${item.color} p-6 text-white`}>
                  <div className="flex items-center justify-center mb-4">
                    {item.icon}
                  </div>
                  <h3 className="text-lg font-bold text-center">{item.title}</h3>
                </div>
                <div className="p-6">
                  <ul className="space-y-2">
                    {item.details.map((detail, i) => (
                      <li key={i} className="text-mauve-700 text-center">
                        {detail}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Contact Form */}
          <div className="grid lg:grid-cols-2 gap-12">
            <motion.div variants={fadeInUp}>
              <div className="bg-white rounded-2xl shadow-lg p-8 border border-mauve-100">
                <div className="flex items-center mb-6">
                  <MessageSquare className="text-mauve-500 mr-3" size={24} />
                  <h2 className="text-2xl font-bold text-mauve-900">Send a Message</h2>
                </div>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-mauve-700 mb-2">
                        First Name
                      </label>
                      <input
                        type="text"
                        required
                        className="w-full px-4 py-3 border border-mauve-200 rounded-lg focus:border-mauve-500 focus:ring-2 focus:ring-mauve-100 outline-none transition"
                        placeholder="Enter your first name"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-mauve-700 mb-2">
                        Last Name
                      </label>
                      <input
                        type="text"
                        required
                        className="w-full px-4 py-3 border border-mauve-200 rounded-lg focus:border-mauve-500 focus:ring-2 focus:ring-mauve-100 outline-none transition"
                        placeholder="Enter your last name"
                      />
                    </div>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-mauve-700 mb-2">
                        Email Address
                      </label>
                      <input
                        type="email"
                        required
                        className="w-full px-4 py-3 border border-mauve-200 rounded-lg focus:border-mauve-500 focus:ring-2 focus:ring-mauve-100 outline-none transition"
                        placeholder="your@email.com"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-mauve-700 mb-2">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        required
                        className="w-full px-4 py-3 border border-mauve-200 rounded-lg focus:border-mauve-500 focus:ring-2 focus:ring-mauve-100 outline-none transition"
                        placeholder="020 XXX XXXX"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-mauve-700 mb-2">
                      Inquiry Topic
                    </label>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {topics.map(topic => (
                        <button
                          key={topic.id}
                          type="button"
                          className={`px-4 py-2 rounded-full text-sm font-medium transition ${
                            selectedTopic === topic.id
                              ? 'bg-mauve-500 text-white'
                              : 'bg-mauve-100 text-mauve-700 hover:bg-mauve-200'
                          }`}
                          onClick={() => setSelectedTopic(topic.id)}
                        >
                          {topic.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-mauve-700 mb-2">
                      Message
                    </label>
                    <textarea
                      required
                      className="w-full px-4 py-3 border border-mauve-200 rounded-lg focus:border-mauve-500 focus:ring-2 focus:ring-mauve-100 outline-none transition min-h-[150px]"
                      placeholder="How can we help you today?"
                    />
                  </div>
                  
                  <button
                    type="submit"
                    disabled={formStatus === 'submitting'}
                    className="w-full btn-primary py-3 text-lg font-semibold flex items-center justify-center"
                  >
                    {formStatus === 'submitting' ? (
                      'Sending Message...'
                    ) : formStatus === 'success' ? (
                      'Message Sent Successfully!'
                    ) : (
                      <>
                        <Send size={20} className="mr-2" />
                        Send Message
                      </>
                    )}
                  </button>
                  
                  <p className="text-center text-sm text-mauve-600">
                    We aim to respond to all inquiries within 2 business hours
                  </p>
                </form>
              </div>
            </motion.div>
            
            <motion.div variants={fadeInUp} className="space-y-8">
              <div className="bg-mauve-50 rounded-2xl p-8">
                <h3 className="text-xl font-bold text-mauve-900 mb-4">
                  Why Choose GetNailedbyAJ?
                </h3>
                <ul className="space-y-4">
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-mauve-500 rounded-full mt-2 mr-3"></div>
                    <span className="text-mauve-700">
                      Certified nail technicians with international training
                    </span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-mauve-500 rounded-full mt-2 mr-3"></div>
                    <span className="text-mauve-700">
                      Hospital-grade sterilization and hygiene protocols
                    </span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-mauve-500 rounded-full mt-2 mr-3"></div>
                    <span className="text-mauve-700">
                      Authentic Ghanaian ingredients in our treatments
                    </span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-mauve-500 rounded-full mt-2 mr-3"></div>
                    <span className="text-mauve-700">
                      Flexible booking options including mobile services
                    </span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-mauve-500 rounded-full mt-2 mr-3"></div>
                    <span className="text-mauve-700">
                      Professional environment in Hatso, Accra
                    </span>
                  </li>
                </ul>
              </div>
              
              <div className="bg-gradient-to-br from-mauve-500 to-mauve-700 rounded-2xl p-8 text-white">
                <h3 className="text-xl font-bold mb-4">Emergency Contact</h3>
                <p className="mb-4">
                  For urgent matters outside business hours, please use our emergency contact number.
                </p>
                <div className="bg-white/20 rounded-lg p-4">
                  <div className="font-bold text-lg">Emergency: 059 419 7765</div>
                  <p className="text-sm opacity-90 mt-1">
                    Available for existing clients with urgent needs
                  </p>
                </div>
              </div>
              
              <div className="bg-white rounded-2xl shadow border border-mauve-100 p-8">
                <h3 className="text-xl font-bold text-mauve-900 mb-4">
                  Visit Our Salon
                </h3>
                <p className="text-mauve-700 mb-4">
                  We recommend booking an appointment before visiting to ensure we can serve you promptly.
                </p>
                <button className="w-full btn-outline">
                  Book Appointment Now
                </button>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </section>
    </motion.main>
  );
};

export default Contact;